import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Shield, Clock, ChevronDown, ChevronUp, Trash2, DollarSign, Eye, FileText, Search, CreditCard, AlertCircle, User, CalendarX2, Tag, UserPlus, UserMinus, LogIn, LogOut, Settings, Plus } from 'lucide-react';
import { propertyAPI } from '../services/api';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => '$' + Math.round(n || 0).toLocaleString('es-CO');

const METODO_LABELS = {
  zelle: 'Zelle', pago_movil: 'Pago Móvil', efectivo: 'Efectivo',
  tarjeta: 'Tarjeta', transferencia: 'Transferencia', paypal: 'PayPal',
  efectivo_usd: 'Efectivo (USD)', efectivo_bs: 'Efectivo (Bs)',
  tarjeta_debito: 'Tarjeta Débito', tarjeta_credito: 'Tarjeta Crédito', otro: 'Otro'
};

const ACTION_LABELS = {
  pago_creado: { label: 'Pago registrado', color: 'bg-green-100 text-green-700', icon: DollarSign },
  pago_eliminado: { label: 'Pago eliminado', color: 'bg-red-100 text-red-700', icon: Trash2 },
  gasto_turno_eliminado: { label: 'Gasto de turno eliminado', color: 'bg-orange-100 text-orange-700', icon: Trash2 },
  gasto_turno_creado: { label: 'Gasto de turno registrado', color: 'bg-orange-100 text-orange-700', icon: Plus },
  reserva_eliminada: { label: 'Reserva eliminada', color: 'bg-rose-100 text-rose-700', icon: CalendarX2 },
  reserva_creada: { label: 'Reserva creada', color: 'bg-blue-100 text-blue-700', icon: CreditCard },
  reserva_modificada: { label: 'Reserva modificada', color: 'bg-blue-50 text-blue-600', icon: Settings },
  tarifa_creada: { label: 'Tarifa creada', color: 'bg-purple-100 text-purple-700', icon: Tag },
  tarifa_modificada: { label: 'Tarifa modificada', color: 'bg-purple-50 text-purple-600', icon: Tag },
  tarifa_eliminada: { label: 'Tarifa eliminada', color: 'bg-purple-100 text-purple-700', icon: Trash2 },
  temporada_modificada: { label: 'Temporada modificada', color: 'bg-amber-100 text-amber-700', icon: Settings },
  temporada_eliminada: { label: 'Temporada eliminada', color: 'bg-amber-100 text-amber-700', icon: Trash2 },
  checkin_realizado: { label: 'Check-in realizado', color: 'bg-green-100 text-green-700', icon: LogIn },
  checkout_realizado: { label: 'Check-out realizado', color: 'bg-teal-100 text-teal-700', icon: LogOut },
  turno_abierto: { label: 'Turno abierto', color: 'bg-cyan-100 text-cyan-700', icon: Clock },
  turno_cerrado: { label: 'Turno cerrado', color: 'bg-cyan-50 text-cyan-600', icon: Clock },
  comision_admin_modificada: { label: 'Comisión admin. modificada', color: 'bg-violet-100 text-violet-700', icon: Settings },
  usuario_creado: { label: 'Usuario creado', color: 'bg-green-100 text-green-700', icon: UserPlus },
  usuario_eliminado: { label: 'Usuario eliminado', color: 'bg-red-100 text-red-700', icon: UserMinus },
  habitacion_modificada: { label: 'Habitación modificada', color: 'bg-indigo-100 text-indigo-700', icon: Settings },
  habitacion_eliminada: { label: 'Habitación eliminada', color: 'bg-red-100 text-red-700', icon: Trash2 },
  limite_habitaciones_modificado: { label: 'Límite habitaciones modificado', color: 'bg-amber-100 text-amber-700', icon: Settings },
};

import { useGlobalProperty } from '@/hooks/useGlobalProperty';

export default function AuditoriasPage() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useGlobalProperty(properties);
  const [tab, setTab] = useState('turnos');
  const [loading, setLoading] = useState(true);

  // Shifts review
  const [shifts, setShifts] = useState([]);
  const [selectedShift, setSelectedShift] = useState(null);
  const [shiftDetail, setShiftDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Audit logs
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Date filters
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    propertyAPI.getAll().then(res => {
      setProperties(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const fetchShifts = useCallback(async () => {
    if (!selectedProperty) return;
    try {
      const params = [];
      if (fechaInicio) params.push(`fecha_inicio=${fechaInicio}`);
      if (fechaFin) params.push(`fecha_fin=${fechaFin}`);
      const res = await axios.get(`${BACKEND_URL}/api/shifts/${selectedProperty}/list`, { headers });
      let data = res.data;
      if (fechaInicio) data = data.filter(s => (s.opened_at || '') >= fechaInicio);
      if (fechaFin) data = data.filter(s => (s.opened_at || '').slice(0, 10) <= fechaFin);
      setShifts(data);
    } catch { setShifts([]); }
  }, [selectedProperty, fechaInicio, fechaFin]);

  const fetchAuditLogs = useCallback(async () => {
    if (!selectedProperty) return;
    setLoadingLogs(true);
    try {
      const params = [];
      if (fechaInicio) params.push(`fecha_inicio=${fechaInicio}`);
      if (fechaFin) params.push(`fecha_fin=${fechaFin}`);
      const res = await axios.get(`${BACKEND_URL}/api/audit-logs/${selectedProperty}${params.length ? '?' + params.join('&') : ''}`, { headers });
      setAuditLogs(res.data);
    } catch { setAuditLogs([]); }
    setLoadingLogs(false);
  }, [selectedProperty, fechaInicio, fechaFin]);

  useEffect(() => {
    if (tab === 'turnos') fetchShifts();
    else fetchAuditLogs();
  }, [tab, fetchShifts, fetchAuditLogs]);

  const loadShiftDetail = async (shift) => {
    setSelectedShift(shift);
    setLoadingDetail(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/audit/shift-detail/${shift.id}`, { headers });
      setShiftDetail(res.data);
    } catch { toast.error('Error al cargar detalle'); }
    setLoadingDetail(false);
  };

  const handleDeletePayment = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/audit/shift-payment/${deleteTarget.id}`, { headers });
      toast.success('Pago eliminado correctamente');
      setDeleteTarget(null);
      if (selectedShift) loadShiftDetail(selectedShift);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar'); }
    setDeleting(false);
  };

  const handleDeleteExpense = async () => {
    if (!deleteTarget || !selectedShift) return;
    setDeleting(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/audit/shift-expense/${selectedShift.id}/${deleteTarget.id}`, { headers });
      toast.success('Gasto eliminado correctamente');
      setDeleteTarget(null);
      loadShiftDetail(selectedShift);
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar'); }
    setDeleting(false);
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' }) : '-';

  if (loading) return (
    <DashboardLayout>
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="auditorias-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Shield className="h-7 w-7" />
              Auditorías
            </h1>
            <p className="text-muted-foreground mt-1">Control de movimientos, pagos y gastos del sistema</p>
          </div>
          {properties.length > 0 && (
            <Select value={selectedProperty} onValueChange={setSelectedProperty}>
              <SelectTrigger className="w-[220px]" data-testid="audit-property-selector">
                <SelectValue placeholder="Selecciona propiedad" />
              </SelectTrigger>
              <SelectContent>
                {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Date Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Desde</Label>
                <Input type="date" value={fechaInicio} onChange={e => setFechaInicio(e.target.value)} className="w-40" data-testid="audit-fecha-inicio" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Hasta</Label>
                <Input type="date" value={fechaFin} onChange={e => setFechaFin(e.target.value)} className="w-40" data-testid="audit-fecha-fin" />
              </div>
              <Button variant="outline" size="sm" onClick={() => { setFechaInicio(''); setFechaFin(''); }} className="text-xs">Limpiar</Button>
              <div className="flex-1" />
              <div className="flex gap-2">
                <Button variant={tab === 'turnos' ? 'default' : 'outline'} size="sm" onClick={() => setTab('turnos')} data-testid="tab-turnos">
                  <Clock className="h-4 w-4 mr-1" /> Revisión de Turnos
                </Button>
                <Button variant={tab === 'logs' ? 'default' : 'outline'} size="sm" onClick={() => setTab('logs')} data-testid="tab-logs">
                  <FileText className="h-4 w-4 mr-1" /> Log de Auditoría
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tab: Revisión de Turnos */}
        {tab === 'turnos' && (
          <div className="space-y-4">
            {shifts.length === 0 ? (
              <Card className="py-12 text-center">
                <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-lg font-medium text-muted-foreground">No hay turnos registrados</p>
              </Card>
            ) : (
              <div className="space-y-3">
                {shifts.map(shift => (
                  <Card key={shift.id} className={`transition-all ${selectedShift?.id === shift.id ? 'ring-2 ring-primary' : ''}`} data-testid={`shift-card-${shift.id}`}>
                    <Collapsible open={selectedShift?.id === shift.id} onOpenChange={(open) => {
                      if (open) loadShiftDetail(shift);
                      else { setSelectedShift(null); setShiftDetail(null); }
                    }}>
                      <CollapsibleTrigger className="w-full">
                        <CardContent className="pt-4 pb-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Badge variant={shift.status === 'open' ? 'default' : 'secondary'}>
                                {shift.status === 'open' ? 'Abierto' : 'Cerrado'}
                              </Badge>
                              <div className="text-left">
                                <p className="text-sm font-medium">{fmtDate(shift.opened_at)}{shift.closed_at ? ` → ${fmtDate(shift.closed_at)}` : ''}</p>
                                <p className="text-xs text-muted-foreground">Operador: {shift.user_name}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-right">
                                <span className="text-sm text-green-600 font-medium">{fmt(shift.total_ingresos)}</span>
                                <span className="text-xs text-muted-foreground mx-1">|</span>
                                <span className="text-sm text-red-500 font-medium">{fmt(shift.total_gastos)}</span>
                              </div>
                              {selectedShift?.id === shift.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                            </div>
                          </div>
                        </CardContent>
                      </CollapsibleTrigger>

                      <CollapsibleContent>
                        <Separator />
                        {loadingDetail ? (
                          <div className="py-8 text-center text-muted-foreground">Cargando detalle...</div>
                        ) : shiftDetail ? (
                          <div className="p-4 space-y-4">
                            {/* Payments Section */}
                            <div>
                              <h4 className="text-sm font-semibold flex items-center gap-1.5 mb-2">
                                <CreditCard className="h-4 w-4 text-green-600" />
                                Pagos del turno ({shiftDetail.payments.length})
                              </h4>
                              {shiftDetail.payments.length === 0 ? (
                                <p className="text-xs text-muted-foreground pl-6">Sin pagos registrados</p>
                              ) : (
                                <div className="space-y-1.5">
                                  {shiftDetail.payments.map(p => (
                                    <div key={p.id} className="flex items-center justify-between bg-green-50/50 border rounded-lg px-3 py-2 text-sm" data-testid={`audit-payment-${p.id}`}>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium">{fmt(p.monto)}</span>
                                          <Badge variant="outline" className="text-[10px]">{METODO_LABELS[p.metodo_pago] || p.metodo_pago}</Badge>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                          {p.guest_name && <span>{p.guest_name}</span>}
                                          {p.room_name && <span> · {p.room_name}</span>}
                                          {p.referencia && <span> · Ref: {p.referencia}</span>}
                                          <span> · {fmtDate(p.created_at)}</span>
                                        </p>
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                                        onClick={(e) => { e.stopPropagation(); setDeleteTarget({ ...p, type: 'payment' }); }}
                                        data-testid={`delete-audit-payment-${p.id}`}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            <Separator />

                            {/* Expenses Section */}
                            <div>
                              <h4 className="text-sm font-semibold flex items-center gap-1.5 mb-2">
                                <AlertCircle className="h-4 w-4 text-red-500" />
                                Gastos del turno ({shiftDetail.gastos.length})
                              </h4>
                              {shiftDetail.gastos.length === 0 ? (
                                <p className="text-xs text-muted-foreground pl-6">Sin gastos registrados</p>
                              ) : (
                                <div className="space-y-1.5">
                                  {shiftDetail.gastos.map(g => (
                                    <div key={g.id} className="flex items-center justify-between bg-red-50/50 border rounded-lg px-3 py-2 text-sm" data-testid={`audit-expense-${g.id}`}>
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                          <span className="font-medium text-red-600">{fmt(g.monto)}</span>
                                          <span className="text-muted-foreground">{g.concepto || 'Sin concepto'}</span>
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                          {g.user_name && <span>Por: {g.user_name}</span>}
                                          <span> · {fmtDate(g.created_at)}</span>
                                        </p>
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        className="text-red-500 hover:text-red-700 hover:bg-red-50 shrink-0"
                                        onClick={(e) => { e.stopPropagation(); setDeleteTarget({ ...g, type: 'expense' }); }}
                                        data-testid={`delete-audit-expense-${g.id}`}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        ) : null}
                      </CollapsibleContent>
                    </Collapsible>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tab: Log de Auditoría */}
        {tab === 'logs' && (
          <Card data-testid="audit-logs-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                <FileText className="h-4 w-4" />
                Registro de Movimientos
              </CardTitle>
              <p className="text-xs text-muted-foreground">Historial permanente de acciones — no se puede eliminar</p>
            </CardHeader>
            <CardContent>
              {loadingLogs ? (
                <div className="py-8 text-center text-muted-foreground">Cargando registros...</div>
              ) : auditLogs.length === 0 ? (
                <div className="py-12 text-center">
                  <Shield className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-lg font-medium text-muted-foreground">Sin registros de auditoría</p>
                  <p className="text-sm text-muted-foreground mt-1">Los movimientos aparecerán aquí conforme se registren</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {auditLogs.map(log => {
                    const config = ACTION_LABELS[log.action] || { label: log.action, color: 'bg-gray-100 text-gray-700', icon: FileText };
                    const Icon = config.icon;
                    return (
                      <div key={log.id} className="flex items-start gap-3 border rounded-lg px-3 py-2.5" data-testid={`audit-log-${log.id}`}>
                        <div className={`p-1.5 rounded-md ${config.color} shrink-0 mt-0.5`}>
                          <Icon className="h-3.5 w-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium">{config.label}</span>
                            {log.details?.monto && (
                              <Badge variant="outline" className="text-xs">{fmt(log.details.monto)}</Badge>
                            )}
                            {log.details?.precio_total && (
                              <Badge variant="outline" className="text-xs">{fmt(log.details.precio_total)}</Badge>
                            )}
                            {log.details?.metodo_pago && (
                              <Badge variant="outline" className="text-[10px]">{METODO_LABELS[log.details.metodo_pago] || log.details.metodo_pago}</Badge>
                            )}
                            {log.details?.concepto && (
                              <span className="text-xs text-muted-foreground">"{log.details.concepto}"</span>
                            )}
                            {log.details?.guest_name && (
                              <span className="text-xs font-medium">{log.details.guest_name}</span>
                            )}
                            {log.details?.room_name && (
                              <Badge variant="outline" className="text-[10px]">{log.details.room_name}</Badge>
                            )}
                            {log.details?.fecha_checkin && log.details?.fecha_checkout && (
                              <span className="text-xs text-muted-foreground">{log.details.fecha_checkin} → {log.details.fecha_checkout}</span>
                            )}
                            {log.details?.nombre && (
                              <span className="text-xs font-medium">"{log.details.nombre}"</span>
                            )}
                            {log.details?.nombre_usuario && (
                              <span className="text-xs font-medium">{log.details.nombre_usuario}</span>
                            )}
                            {log.details?.email && !log.details?.guest_name && (
                              <span className="text-xs text-muted-foreground">{log.details.email}</span>
                            )}
                            {log.details?.porcentaje !== undefined && (
                              <Badge variant="outline" className="text-xs">{log.details.porcentaje}%</Badge>
                            )}
                            {log.details?.total_ingresos !== undefined && (
                              <span className="text-xs text-green-600">Ingresos: {fmt(log.details.total_ingresos)}</span>
                            )}
                            {log.details?.cambios && (
                              <span className="text-xs text-muted-foreground">Campos: {log.details.cambios.filter(c => c !== 'updated_at').join(', ')}</span>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1">
                            <User className="h-3 w-3" />
                            <span className="font-medium">{log.user_name}</span>
                            <span>·</span>
                            <span>{fmtDate(log.created_at)}</span>
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                <AlertCircle className="h-5 w-5" />
                {deleteTarget?.type === 'payment' ? '¿Eliminar este pago?' : '¿Eliminar este gasto?'}
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2">
                  <p>Esta acción quedará registrada en el log de auditoría con tu nombre de usuario.</p>
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex justify-between text-sm">
                      <span>{deleteTarget?.type === 'payment' ? 'Monto del pago' : 'Monto del gasto'}:</span>
                      <span className="font-bold text-red-700">{fmt(deleteTarget?.monto)}</span>
                    </div>
                    {deleteTarget?.concepto && (
                      <div className="text-sm mt-1 text-muted-foreground">Concepto: {deleteTarget.concepto}</div>
                    )}
                    {deleteTarget?.metodo_pago && (
                      <div className="text-sm mt-1 text-muted-foreground">Método: {METODO_LABELS[deleteTarget.metodo_pago] || deleteTarget.metodo_pago}</div>
                    )}
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                className="bg-red-600 hover:bg-red-700"
                onClick={deleteTarget?.type === 'payment' ? handleDeletePayment : handleDeleteExpense}
                disabled={deleting}
                data-testid="confirm-audit-delete"
              >
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
